import * as _ from 'lodash';
import { defaults, clone } from 'lodash';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import { formatNumber as formatNumber$1 } from '@angular/common';

function assertNever(item) {
    throw new Error(`Unexpected object ${item}`);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function assert(value, message) {
    if (!value) {
        throw new Error(`AssertionError: ${message}` || 'AssertionError');
    }
}

class EnumHelper {
    /**
     * Given an enum name (or key), returns the enum value
     * @param enumClass Enum type
     * @param name name
     * @returns value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static fromName(enumClass, name) {
        const value = enumClass[name];
        if (value !== undefined && !_.isNumber(enumClass[value])) {
            return value;
        }
        return null;
    }
    /**
     * Given an enum value, returns the value only if it exists in the enum
     * @param enumClass Enum type
     * @param value value
     * @returns value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static fromValue(enumClass, value) {
        for (const k of Object.keys(enumClass)) {
            if (isNaN(Number(k))) {
                if (enumClass[k] === value) {
                    return value;
                }
            }
        }
        return null;
    }
}

function format(template, args) {
    const message = Object.keys(args || {}).reduce((acc, item) => acc.replace(`{{${item}}}`, args[item]), template || '');
    return message;
}

function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
}
/**
 * Generates a globally unique identifier.
 * @see https://en.wikipedia.org/wiki/Universally_unique_identifier
 */
function guid() {
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

/**
 * Converts an object properties to a {@link HttpParams} object.
 * If an initial {@link HttpParams} object is specified, object properties are merged into this object.
 * @param obj The object to convert.
 * @param initialParams The {@link HttpParams} object to use as initial value.
 * @returns A {@link HttpParams} object containing source object properties as parameters.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
function toHttpParams(obj, initialParams) {
    const result = Object.keys(obj).reduce((p, key) => {
        if (obj[key] != null) {
            return p.set(key, obj[key].toString());
        }
        return p;
    }, initialParams || new HttpParams());
    return result;
}
/**
 * Converts an object properties to a {@link HttpHeaders} object.
 * If an initial {@link HttpHeaders} object is specified, object properties are merged into this object.
 * @param obj The object to convert.
 * @param initialParams The {@link HttpHeaders} object to use as initial value.
 * @returns A {@link HttpHeaders} object containing source object properties as parameters.
 */
function toHttpHeaders(obj, initialParams) {
    const result = Object.keys(obj).reduce((p, key) => {
        if (obj[key] != null) {
            return p.set(key, obj[key].toString());
        }
        return p;
    }, initialParams || new HttpHeaders());
    return result;
}

/**
 * Use correctedFloat to fix the result of a floating-point operation
 * inpired from https://stackoverflow.com/questions/588004/is-floating-point-math-broken/51723472#51723472
 */
const FLOATING_POINT_ERROR_DIGIT = 15;
const MIN_TO_FIXED_MAX_PARAM = 0;
const MAX_TO_FIXED_MAX_PARAM = 20;
function correctedFloat(value) {
    const integerDigitCount = value === 0 ? 1 : Math.floor(Math.log10(Math.abs(value))) + 1;
    const toFixedDigitCount = Math.min(Math.max(MIN_TO_FIXED_MAX_PARAM, FLOATING_POINT_ERROR_DIGIT - integerDigitCount), MAX_TO_FIXED_MAX_PARAM);
    return parseFloat(value.toFixed(toFixedDigitCount));
}
/**
 * RoundTo takes in two numbers, the value to be rounded and the number of decimal places to include
 * @value value to be rounded
 * @digits number of decimal places to include
 */
function roundTo(value, digits) {
    const multiplier = Math.pow(10, digits);
    return Math.sign(value) * correctedFloat(Math.round(correctedFloat(Math.abs(value) * multiplier)) / multiplier);
}
/**
 * Formats to string a number using the user's local format and params.
 * @param value The value to format to string
 * @param locale The user's locale
 * @param params The formatting parameters.
 * Default params are:
 *  -  minIntegerDigits: 1
 *  -  maxFractionDigits: 2
 *  If no minFractionDigits is provided, it will use the maxFractionDigits value (or default).
 */
function formatNumber(value, locale, params) {
    const { minIntegerDigits, minFractionDigits, maxFractionDigits } = defaults({}, params, {
        minIntegerDigits: 1,
        minFractionDigits: params && params.minFractionDigits,
        maxFractionDigits: params && params.maxFractionDigits
    }, {
        minFractionDigits: params && params.maxFractionDigits
    }, {
        minFractionDigits: 2,
        maxFractionDigits: 2
    });
    return value != null
        ? formatNumber$1(value, locale, `${minIntegerDigits}.${minFractionDigits}-${maxFractionDigits}`)
        : null;
}

/**
 * Converts a typescript map to its object equivalent.
 *
 * ### Sample
 *
 * ```ts
 * const map = new Map([['key1', 'value1'], ['key2', 'value2']]);
 * const obj = convertMapToObject(map);
 *
 * console.log(obj);
 * // output: { "key1": "value1", "key2": "value2" }
 * ```
 *
 * @param map The map to convert.
 * @returns The object representation of a map.
 */
function convertMapToObject(map) {
    const result = {};
    map.forEach((value, key) => (result[key] = value));
    return result;
}

/**
 * KeyboardEvent key codes
 */
var KeyCode;
(function (KeyCode) {
    KeyCode[KeyCode["Backspace"] = 8] = "Backspace";
    KeyCode[KeyCode["Tab"] = 9] = "Tab";
    KeyCode[KeyCode["Enter"] = 13] = "Enter";
    KeyCode[KeyCode["Shift"] = 16] = "Shift";
    KeyCode[KeyCode["Ctrl"] = 17] = "Ctrl";
    KeyCode[KeyCode["Alt"] = 18] = "Alt";
    KeyCode[KeyCode["PauseBreak"] = 19] = "PauseBreak";
    KeyCode[KeyCode["CapsLock"] = 20] = "CapsLock";
    KeyCode[KeyCode["Escape"] = 27] = "Escape";
    KeyCode[KeyCode["Space"] = 32] = "Space";
    KeyCode[KeyCode["PageUp"] = 33] = "PageUp";
    KeyCode[KeyCode["PageDown"] = 34] = "PageDown";
    KeyCode[KeyCode["End"] = 35] = "End";
    KeyCode[KeyCode["Home"] = 36] = "Home";
    KeyCode[KeyCode["LeftArrow"] = 37] = "LeftArrow";
    KeyCode[KeyCode["UpArrow"] = 38] = "UpArrow";
    KeyCode[KeyCode["RightArrow"] = 39] = "RightArrow";
    KeyCode[KeyCode["DownArrow"] = 40] = "DownArrow";
    KeyCode[KeyCode["Insert"] = 45] = "Insert";
    KeyCode[KeyCode["Delete"] = 46] = "Delete";
    KeyCode[KeyCode["Zero"] = 48] = "Zero";
    KeyCode[KeyCode["ClosedParen"] = 48] = "ClosedParen";
    KeyCode[KeyCode["One"] = 49] = "One";
    KeyCode[KeyCode["ExclamationMark"] = 49] = "ExclamationMark";
    KeyCode[KeyCode["Two"] = 50] = "Two";
    KeyCode[KeyCode["AtSign"] = 50] = "AtSign";
    KeyCode[KeyCode["Three"] = 51] = "Three";
    KeyCode[KeyCode["PoundSign"] = 51] = "PoundSign";
    KeyCode[KeyCode["Hash"] = 51] = "Hash";
    KeyCode[KeyCode["Four"] = 52] = "Four";
    KeyCode[KeyCode["DollarSign"] = 52] = "DollarSign";
    KeyCode[KeyCode["Five"] = 53] = "Five";
    KeyCode[KeyCode["PercentSign"] = 53] = "PercentSign";
    KeyCode[KeyCode["Six"] = 54] = "Six";
    KeyCode[KeyCode["Caret"] = 54] = "Caret";
    KeyCode[KeyCode["Hat"] = 54] = "Hat";
    KeyCode[KeyCode["Seven"] = 55] = "Seven";
    KeyCode[KeyCode["Ampersand"] = 55] = "Ampersand";
    KeyCode[KeyCode["Eight"] = 56] = "Eight";
    KeyCode[KeyCode["Star"] = 56] = "Star";
    KeyCode[KeyCode["Asterik"] = 56] = "Asterik";
    KeyCode[KeyCode["Nine"] = 57] = "Nine";
    KeyCode[KeyCode["OpenParen"] = 57] = "OpenParen";
    KeyCode[KeyCode["A"] = 65] = "A";
    KeyCode[KeyCode["B"] = 66] = "B";
    KeyCode[KeyCode["C"] = 67] = "C";
    KeyCode[KeyCode["D"] = 68] = "D";
    KeyCode[KeyCode["E"] = 69] = "E";
    KeyCode[KeyCode["F"] = 70] = "F";
    KeyCode[KeyCode["G"] = 71] = "G";
    KeyCode[KeyCode["H"] = 72] = "H";
    KeyCode[KeyCode["I"] = 73] = "I";
    KeyCode[KeyCode["J"] = 74] = "J";
    KeyCode[KeyCode["K"] = 75] = "K";
    KeyCode[KeyCode["L"] = 76] = "L";
    KeyCode[KeyCode["M"] = 77] = "M";
    KeyCode[KeyCode["N"] = 78] = "N";
    KeyCode[KeyCode["O"] = 79] = "O";
    KeyCode[KeyCode["P"] = 80] = "P";
    KeyCode[KeyCode["Q"] = 81] = "Q";
    KeyCode[KeyCode["R"] = 82] = "R";
    KeyCode[KeyCode["S"] = 83] = "S";
    KeyCode[KeyCode["T"] = 84] = "T";
    KeyCode[KeyCode["U"] = 85] = "U";
    KeyCode[KeyCode["V"] = 86] = "V";
    KeyCode[KeyCode["W"] = 87] = "W";
    KeyCode[KeyCode["X"] = 88] = "X";
    KeyCode[KeyCode["Y"] = 89] = "Y";
    KeyCode[KeyCode["Z"] = 90] = "Z";
    KeyCode[KeyCode["LeftWindowKey"] = 91] = "LeftWindowKey";
    KeyCode[KeyCode["RightWindowKey"] = 92] = "RightWindowKey";
    KeyCode[KeyCode["SelectKey"] = 93] = "SelectKey";
    KeyCode[KeyCode["Numpad0"] = 96] = "Numpad0";
    KeyCode[KeyCode["Numpad1"] = 97] = "Numpad1";
    KeyCode[KeyCode["Numpad2"] = 98] = "Numpad2";
    KeyCode[KeyCode["Numpad3"] = 99] = "Numpad3";
    KeyCode[KeyCode["Numpad4"] = 100] = "Numpad4";
    KeyCode[KeyCode["Numpad5"] = 101] = "Numpad5";
    KeyCode[KeyCode["Numpad6"] = 102] = "Numpad6";
    KeyCode[KeyCode["Numpad7"] = 103] = "Numpad7";
    KeyCode[KeyCode["Numpad8"] = 104] = "Numpad8";
    KeyCode[KeyCode["Numpad9"] = 105] = "Numpad9";
    KeyCode[KeyCode["Multiply"] = 106] = "Multiply";
    KeyCode[KeyCode["Add"] = 107] = "Add";
    KeyCode[KeyCode["Subtract"] = 109] = "Subtract";
    KeyCode[KeyCode["DecimalPoint"] = 110] = "DecimalPoint";
    KeyCode[KeyCode["Divide"] = 111] = "Divide";
    KeyCode[KeyCode["F1"] = 112] = "F1";
    KeyCode[KeyCode["F2"] = 113] = "F2";
    KeyCode[KeyCode["F3"] = 114] = "F3";
    KeyCode[KeyCode["F4"] = 115] = "F4";
    KeyCode[KeyCode["F5"] = 116] = "F5";
    KeyCode[KeyCode["F6"] = 117] = "F6";
    KeyCode[KeyCode["F7"] = 118] = "F7";
    KeyCode[KeyCode["F8"] = 119] = "F8";
    KeyCode[KeyCode["F9"] = 120] = "F9";
    KeyCode[KeyCode["F10"] = 121] = "F10";
    KeyCode[KeyCode["F11"] = 122] = "F11";
    KeyCode[KeyCode["F12"] = 123] = "F12";
    KeyCode[KeyCode["NumLock"] = 144] = "NumLock";
    KeyCode[KeyCode["ScrollLock"] = 145] = "ScrollLock";
    KeyCode[KeyCode["SemiColon"] = 186] = "SemiColon";
    KeyCode[KeyCode["Equals"] = 187] = "Equals";
    KeyCode[KeyCode["Comma"] = 188] = "Comma";
    KeyCode[KeyCode["Dash"] = 189] = "Dash";
    KeyCode[KeyCode["Period"] = 190] = "Period";
    KeyCode[KeyCode["UnderScore"] = 189] = "UnderScore";
    KeyCode[KeyCode["PlusSign"] = 187] = "PlusSign";
    KeyCode[KeyCode["ForwardSlash"] = 191] = "ForwardSlash";
    KeyCode[KeyCode["Tilde"] = 192] = "Tilde";
    KeyCode[KeyCode["GraveAccent"] = 192] = "GraveAccent";
    KeyCode[KeyCode["OpenBracket"] = 219] = "OpenBracket";
    KeyCode[KeyCode["ClosedBracket"] = 221] = "ClosedBracket";
    KeyCode[KeyCode["Quote"] = 222] = "Quote";
})(KeyCode || (KeyCode = {}));

/**
 * Number parsing and formating Helper for a specific locale.
 */
class NumberHelper {
    /**
     * @param locale The local (for instance: 'en-US')
     */
    constructor(locale) {
        // sample input for configuring locale parameters
        this._fullStop = '.';
        this._sampleNumber = -1234.056789;
        this._sampleExpectedLength = 13;
        this._sampleDecimalPlaceCharIndex = 6;
        this._sampleGroupCharIndex = 2;
        this._numberMap = {};
        this._removeNonBreakingSpace = false;
        this.locale = locale;
    }
    /**
     * Gets the decimal place character.
     * If the locale is 'en' will return '.'.
     */
    get decimalPlaceCharacter() {
        return this._decimalPlaceCharacter;
    }
    /**
     * Gets the group seraparator character.
     * If the locale is 'en', will return ','.
     */
    get groupCharacter() {
        return this._groupCharacter;
    }
    /**
     * Gets the current locale (for instance: 'en-US')
     */
    get locale() {
        return this._locale;
    }
    /**
     * Sets the locale for the service.
     * @param localeString The local (for instance: 'en-US')
     */
    set locale(localeString) {
        this._locale = localeString;
        this.configureLocale();
    }
    ///// public methods /////
    /**
     * Formats the number according to the specified options.
     *
     * @param value the number to format
     * @param options  either the number of decimal places required or full IFormatOptions
     */
    format(value, options) {
        this.checkLocaleIsSet();
        const localeFormatters = NumberHelper._localeFormatters[this._locale];
        let formatter;
        if (options === undefined) {
            //   formattedValue = NumberHelper._defaultFormatter.format(value);
            formatter = localeFormatters.defaultFormatter;
        }
        else if (typeof options === 'number') {
            formatter = localeFormatters.getDecimalPlaceFormatter(options);
        }
        else if (Object.keys(options).length === 2 &&
            options.maximumFractionDigits !== undefined &&
            options.maximumFractionDigits === options.minimumFractionDigits) {
            formatter = localeFormatters.getDecimalPlaceFormatter(options.maximumFractionDigits);
        }
        else {
            const fullOptions = this.mergeWithDefaultOptions(options);
            formatter = localeFormatters.getCustomFormatter(fullOptions);
        }
        const formattedValue = formatter.format(value);
        // if we are dealing with a locale that has spaces as it's group
        // and we are dealing with amount with more than 3 characters
        // (we could do checks to see if there was a decimal value but
        // then that is even more operations which defeats the purpose)
        if (this._removeNonBreakingSpace && formattedValue.length > 3) {
            // returning a non breaking space character can result in
            // display issues for e.g. fr-FR when it is used as a parameter
            // for a translated string (which results in &nbsp; being shown).
            return formattedValue.replace(/\xA0/g, this._groupCharacter);
        }
        return formattedValue;
    }
    /**
     * Parses a string representation of a number and returns a number type.
     * Assumes the string representation conforms to the current locale.
     * @param value
     */
    parse(value) {
        this.checkLocaleIsSet();
        const cleaned = this.replaceLocaleCharacters(value);
        const translated = this.translate(cleaned);
        return this.parseNumberString(translated);
    }
    ///// private methods /////
    configureLocale() {
        const formatter = new Intl.NumberFormat(this._locale, { maximumFractionDigits: 20 });
        const sampleString = formatter.format(this._sampleNumber);
        let decimalCharIndex = this._sampleDecimalPlaceCharIndex;
        let groupCharIndex = this._sampleGroupCharIndex;
        if (sampleString.length < this._sampleExpectedLength) {
            decimalCharIndex--;
            groupCharIndex = null;
        }
        this._groupCharacter = groupCharIndex ? sampleString[groupCharIndex] : '';
        this._decimalPlaceCharacter = sampleString[decimalCharIndex];
        this._groupRegEx = null;
        if (this._decimalPlaceCharacter !== this._fullStop) {
            this._decimalPlaceRegEx = new RegExp(this._decimalPlaceCharacter, 'g');
        }
        if (this._groupCharacter.length === 1) {
            const charCode = this.groupCharacter.charCodeAt(0);
            switch (charCode) {
                case KeyCode.Space:
                    this._groupRegEx = new RegExp('\\s+', 'g');
                    this._removeNonBreakingSpace = true;
                    // since we are removing the non-breaking space for display we also need to update
                    // the group character to match so it uses an empty string rather than the non-breaking
                    // space character it originally found
                    this._groupCharacter = ' ';
                    break;
                default:
                    this._groupRegEx = new RegExp(this._groupCharacter, 'g');
            }
        }
        // TODO
        this.createTranslationMap(sampleString);
        NumberHelper._localeFormatters[this._locale] = new LocaleFormatters(this._locale, this.getDefaultOptions());
    }
    getDefaultOptions() {
        return {
            style: 'decimal',
            useGrouping: true
        };
    }
    checkLocaleIsSet() {
        if (this._locale === null || this._locale === undefined) {
            throw new Error(NumberHelper.LocaleNotSetError);
        }
    }
    mergeWithDefaultOptions(options) {
        const combined = Object.assign(Object.assign({}, this.getDefaultOptions()), options);
        return combined;
    }
    replaceLocaleCharacters(value) {
        if (value === null || value === undefined || value.trim() === '') {
            return value;
        }
        let cleaned = value;
        if (this._groupRegEx) {
            cleaned = cleaned.replace(this._groupRegEx, '');
        }
        if (this._decimalPlaceRegEx) {
            cleaned = cleaned.replace(this._decimalPlaceRegEx, this._fullStop);
        }
        return cleaned;
    }
    // eslint-disable-next-line complexity
    createTranslationMap(sample) {
        // '-1234.056789';
        if (sample[1] === '1') {
            this._requiresTranslation = false;
            return;
        }
        this._requiresTranslation = true;
        const hasGroupChar = this._groupCharacter.length !== 0;
        this._numberMap[sample[0]] = '-';
        this._numberMap[hasGroupChar ? sample[7] : sample[6]] = '0';
        this._numberMap[sample[1]] = '1';
        this._numberMap[hasGroupChar ? sample[3] : sample[2]] = '2';
        this._numberMap[hasGroupChar ? sample[4] : sample[3]] = '3';
        this._numberMap[hasGroupChar ? sample[5] : sample[4]] = '4';
        this._numberMap[hasGroupChar ? sample[8] : sample[7]] = '5';
        this._numberMap[hasGroupChar ? sample[9] : sample[8]] = '6';
        this._numberMap[hasGroupChar ? sample[10] : sample[9]] = '7';
        this._numberMap[hasGroupChar ? sample[11] : sample[10]] = '8';
        this._numberMap[hasGroupChar ? sample[12] : sample[11]] = '9';
    }
    parseNumberString(value) {
        const result = parseFloat(value);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!isNaN(result) && !isFinite(value)) {
            return NaN;
        }
        return result;
    }
    translate(value) {
        if (!this._requiresTranslation) {
            return value;
        }
        let translated = value;
        for (let i = 0; i < translated.length; i++) {
            const newChar = this._numberMap[translated[i]];
            if (newChar !== undefined) {
                translated = translated.substr(0, i) + newChar + translated.substr(i + 1);
            }
        }
        return translated;
    }
}
NumberHelper.LocaleNotSetError = 'You must call setLocale before using the NumberHelper';
// map of locale formatters
NumberHelper._localeFormatters = {};
/**
 * Helper class containing the different decimal place and custom formatters
 * for a given locale and options.
 */
class LocaleFormatters {
    constructor(_locale, _options) {
        this._locale = _locale;
        this._options = _options;
        // decimal place formatters
        this._decimalPlaceFormatters = {};
        this._decimalFormattersCount = 0;
        // custom formatters
        this._customFormatters = {};
        this._customFormattersCount = 0;
    }
    get defaultFormatter() {
        if (!this._defaultFormatter) {
            this._defaultFormatter = this.createFormatter(this._options);
        }
        return this._defaultFormatter;
    }
    ///// public methods /////
    /**
     * Gets the specific formatter for a given decimal place.
     * @param decimalPlace The decimal place
     */
    getDecimalPlaceFormatter(decimalPlace) {
        if (!this._decimalPlaceFormatters[decimalPlace]) {
            const newDecimalFormatter = this.createFormatter(this.getOptions(decimalPlace));
            this._decimalPlaceFormatters[decimalPlace] = newDecimalFormatter;
            this._decimalFormattersCount++;
            if (this._decimalFormattersCount > LocaleFormatters.MAX_CACHED_FORMATTERS) {
                const deleteCandidate = Number(Object.keys(this._decimalPlaceFormatters)[0]);
                delete this._decimalPlaceFormatters[deleteCandidate];
                this._decimalFormattersCount--;
            }
        }
        return this._decimalPlaceFormatters[decimalPlace];
    }
    /**
     * Gets the specific formatter for given options.
     * @param options The options.
     */
    getCustomFormatter(options) {
        const key = `${this._locale}:${this.hashOptions(options)}`;
        const existing = !!this._customFormatters[key];
        if (existing) {
            return this._customFormatters[key];
        }
        const formatter = this.createFormatter(options);
        this._customFormatters[key] = formatter;
        this._customFormattersCount++;
        if (this._customFormattersCount > LocaleFormatters.MAX_CACHED_FORMATTERS) {
            const deleteCandidate = Object.keys(this._customFormatters)[0];
            delete this._customFormatters[deleteCandidate];
            this._customFormattersCount--;
        }
        return formatter;
    }
    ///// private methods /////
    createFormatter(options) {
        return new Intl.NumberFormat(this._locale, options);
    }
    getOptions(decimalPlaces) {
        const options = clone(this._options);
        if (decimalPlaces) {
            options.minimumFractionDigits = decimalPlaces;
            options.maximumFractionDigits = decimalPlaces;
        }
        return options;
    }
    hashOptions(object) {
        return JSON.stringify(object, Object.keys(object).sort());
    }
}
LocaleFormatters.MAX_CACHED_FORMATTERS = 10;

const SHARE_RESOURCE_NAME = 'sdppWebSharedResources';
class ShareResourceHelper {
    static save(key, value) {
        window[SHARE_RESOURCE_NAME] = window[SHARE_RESOURCE_NAME] || {};
        window[SHARE_RESOURCE_NAME][key] = value;
    }
    static get(key) {
        return window[SHARE_RESOURCE_NAME] && window[SHARE_RESOURCE_NAME][key];
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { EnumHelper, KeyCode, NumberHelper, ShareResourceHelper, assert, assertNever, convertMapToObject, correctedFloat, format, formatNumber, guid, roundTo, toHttpHeaders, toHttpParams };
